import { Maximize01 } from '@timelyapp/icons/line-icons';
import { RadixTooltip } from '@timelyapp/react';
import { useEffect, useRef, useState } from 'react';
import type { Client } from 'types/client';
import { AgentProjectColorPicker } from './AgentProjectColorPicker';
import styles from './AgentProjectPopover.module.scss';

export interface ProjectCreation {
  loadClients: () => Promise<Client[]>;
  create: (name: string, clientId: number, color: string | null) => Promise<number>;
  openFull: (name: string, client: Client | undefined, color: string | null) => void;
}
export function AgentProjectCreateForm({
  initialName,
  creation,
  onAssign,
  onBack,
  onFull,
  onBusyChange,
}: {
  initialName: string;
  creation: ProjectCreation;
  onAssign: (id: number) => Promise<void>;
  onBack: () => void;
  onFull: (name: string, client: Client | undefined, color: string | null) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [color, setColor] = useState<string | null>(null);
  const [name, setName] = useState(initialName);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [createdId, setCreatedId] = useState<number | null>(null);
  const loadClients = useRef(creation.loadClients);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries a failed client request.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    loadClients
      .current()
      .then(items => {
        if (!mounted) return;
        setClients(items);
        setClientId(
          items.reduce<Client | undefined>(
            (latest, c) => (!latest || c.id > latest.id ? c : latest),
            undefined
          )?.id ?? null
        );
      })
      .catch(() => {
        if (mounted) setError('Could not load clients. Try again.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [attempt]);
  const submit = async () => {
    if (busy || !name.trim() || clientId === null) return;
    setBusy(true);
    onBusyChange(true);
    setError('');
    let id = createdId;
    try {
      if (id === null) {
        id = await creation.create(name.trim(), clientId, color);
        setCreatedId(id);
      }
      await onAssign(id);
    } catch (e) {
      setError(
        id !== null
          ? 'Project created, but could not be assigned. Retry without creating another project.'
          : (e as Error).message || 'Could not create project. Try again.'
      );
    } finally {
      setBusy(false);
      onBusyChange(false);
    }
  };
  const selectedClient = clients.find(c => c.id === clientId);
  return (
    <form
      className={styles.createForm}
      onSubmit={e => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className={styles.createToolbar}>
        <button type="button" className={styles.textButton} onClick={onBack} disabled={busy}>
          ← Back to projects
        </button>
        {createdId === null && (
          <RadixTooltip tooltip="Open full project setup" side="top" delayDuration={0}>
            <button
              type="button"
              className={styles.fullSetupButton}
              aria-label="Open full project setup"
              disabled={busy}
              onClick={() =>
                onFull(
                  name.trim(),
                  clients.find(c => c.id === clientId),
                  color
                )
              }
            >
              <Maximize01 width={18} height={18} aria-hidden="true" />
            </button>
          </RadixTooltip>
        )}
      </div>
      <div className={styles.projectNameAndColor}>
        <label>
          Project name
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={busy || createdId !== null}
            placeholder="e.g. Website redesign"
            required
          />
        </label>
        <AgentProjectColorPicker
          color={color}
          clientColor={selectedClient?.color}
          onChange={setColor}
          disabled={busy || createdId !== null || loading}
        />
      </div>
      <label>
        Client
        <select
          value={clientId ?? ''}
          onChange={e => setClientId(Number(e.target.value))}
          disabled={loading || busy || createdId !== null}
          required
        >
          <option value="" disabled>
            {loading ? 'Loading clients…' : 'Select a client'}
          </option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      {!loading && !clients.length && !error && (
        <p className={styles.muted}>A client is required. Add one in the full project setup.</p>
      )}
      {error && (
        <p role="alert" className={styles.creationError}>
          {error}
        </p>
      )}
      {!loading && !clients.length && error && (
        <button type="button" className={styles.textButton} onClick={() => setAttempt(a => a + 1)}>
          Retry loading clients
        </button>
      )}
      <button
        className={styles.primary}
        type="submit"
        disabled={loading || busy || !name.trim() || clientId === null}
      >
        {busy ? 'Saving…' : createdId !== null ? 'Retry assignment' : 'Create & assign'}
      </button>
    </form>
  );
}
